import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import DriverSelect from "@/components/ui/driver-select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import FuelProviderSelect from "@/components/ui/fuel-providers-select";
import FuelTypesSelect from "@/components/ui/fuel-types-select";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { errorHandler } from "@/lib/utils";
import { fuellingService } from "@/services/api/fuellings";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import {
  useForm,
  type SubmitErrorHandler,
  type Resolver,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

const useFuellingFormSubmit = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (id: number | undefined, values: FormValues) => {
    setLoading(true);
    try {
      if (id) {
        await fuellingService.update(id, values);
      } else {
        await fuellingService.store(values);
      }
      toast.success("Abastecimento cadastrado com sucesso!");
      navigate("/fuellings");
    } catch (err) {
      errorHandler(err, "Erro ao salvar abastecimento");
    } finally {
      setLoading(false);
    }
  };

  return { handleSubmit, loading };
};

const formSchema = z.object({
  vehicle_id: z.number().min(1, "Selecione um veículo"),
  driver_id: z.number().min(1, "Selecione um motorista"),
  fuel_type_id: z
    .number({
      message: "Selecione um tipo de combustível",
    })
    .min(1, "Selecione um tipo de combustível"),
  partial: z.union([z.literal(0), z.literal(1)]).default(0),
  odometer: z.number().default(0),
  amount: z.number().default(0),
  liters: z.number().default(0),
  arla_liters: z.number().nullable().default(0),
  arla_price: z.number().nullable().default(0),
  invoice_number: z.coerce.number().optional(),
  provider_id: z.number().nullable(),
  date: z.string(),
  hour: z.string(),
  price_per_liter: z.number().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const FuellingsForm = () => {
  const [selectedFuelProvider, setSelectedFuelProvider] = useState<
    number | undefined
  >();
  const [pricePerLiter, setPricePerLiter] = useState<number | null>(null);
  const [selectedFuelType, setSelectedFuelType] = useState<number | null>(null);
  const { id } = useParams();
  const { handleSubmit, loading } = useFuellingFormSubmit();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    // ponytail: resolvers 5 types the raw input apart from the parsed
    // output; the form keeps the parsed type, as it did on resolvers 3.
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues: {
      vehicle_id: 0,
      driver_id: 0,
      fuel_type_id: undefined,
      partial: 0,
      odometer: undefined,
      amount: 0,
      liters: 0,
      arla_liters: 0,
      arla_price: 0,
      invoice_number: undefined,
      provider_id: null,
      date: format(new Date(), "yyyy-MM-dd"),
      hour: new Date().toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      price_per_liter: 0,
    },
  });

  const recalculatePricePerLiter = (amount: number, liters: number) => {
    if (amount && liters && liters !== 0) {
      const calculatedPrice = amount / liters;
      setPricePerLiter(calculatedPrice);
    } else {
      setPricePerLiter(null);
    }
  };

  useEffect(() => {
    const amount = form.watch("amount");
    const liters = form.watch("liters");
    recalculatePricePerLiter(amount, liters);
  }, [form.watch("amount"), form.watch("liters")]);

  const isDieselSelected = selectedFuelType === 4;

  useEffect(() => {
    const loadFuelling = async () => {
      if (id) {
        try {
          const response = await fuellingService.show(Number(id));
          const fuellingData = response.data;
          // `date_time` is a timezone-less "yyyy-MM-dd HH:mm:ss"; read it as
          // local time instead of letting `new Date()` guess.
          const dateTime = parse(
            fuellingData.date_time,
            "yyyy-MM-dd HH:mm:ss",
            new Date(),
          );

          form.reset({
            vehicle_id: fuellingData.vehicle_id,
            driver_id: fuellingData.driver_id,
            fuel_type_id: fuellingData.fuel_type_id,
            partial: fuellingData.partial as 0 | 1,
            odometer: Number(fuellingData.odometer) || 0,
            liters: Number(fuellingData.liters) || 0,
            amount: Number(fuellingData.amount) || 0,
            arla_liters: fuellingData.arla_liters
              ? Number(fuellingData.arla_liters)
              : 0,
            arla_price: fuellingData.arla_price
              ? Number(fuellingData.arla_price)
              : 0,
            invoice_number: fuellingData.invoice_number
              ? Number(fuellingData.invoice_number)
              : undefined,
            provider_id: fuellingData.client_provider_id,
            date: format(dateTime, "yyyy-MM-dd"),
            hour: format(dateTime, "HH:mm"),
            price_per_liter: Number(fuellingData.price_per_liter),
          });

          setSelectedFuelType(fuellingData.fuel_type_id);
          setSelectedFuelProvider(fuellingData.client_provider_id);
        } catch (err) {
          errorHandler(err, "Erro ao carregar abastecimento");
          navigate("/fuellings");
        }
      }
    };

    loadFuelling();
  }, [id, form.reset, navigate]);

  const onSubmit = async (values: FormValues) => {
    await handleSubmit(id ? Number(id) : undefined, values);
  };

  const onError: SubmitErrorHandler<FormValues> = (errors) => {
    console.error("Form errors:", errors);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>
          {id ? "Editar Abastecimento" : "Novo Abastecimento"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, onError)}
            className="space-y-6"
          >
            {/* Form fields remain the same... */}
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="driver_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motorista</FormLabel>
                  <FormControl>
                    <DriverSelect
                      value={field.value}
                      onChange={(value) => field.onChange(Number(value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="partial"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de abastecimento</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value.toString()}
                      onValueChange={(value) => field.onChange(Number(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Completo</SelectItem>
                        <SelectItem value="1">Parcial</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fuel_type_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de combustível</FormLabel>
                  <FormControl>
                    <FuelTypesSelect
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedFuelType(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="odometer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Odômetro</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      value={field.value ? field.value.toString() : ""}
                      onChange={(e) => {
                        let inputValue = e.target.value;
                        inputValue = inputValue.replace(/\D/g, "");
                        if (!inputValue) {
                          field.onChange(0);
                          return;
                        }
                        const numberValue = Number.parseInt(inputValue, 10);
                        field.onChange(numberValue);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor (R$)</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value ?? 0}
                      onChange={(value) => field.onChange(Number(value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="liters"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Litros</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="0,00"
                      value={Number(field.value || 0)
                        .toFixed(3)
                        .replace(".", ",")}
                      onChange={(e) => {
                        let inputValue = e.target.value;
                        inputValue = inputValue.replace(/\D/g, "");
                        const newValue = inputValue
                          ? Number.parseInt(inputValue, 10) / 1000
                          : 0;
                        field.onChange(newValue);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>R$/Litros</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  value={pricePerLiter !== null ? pricePerLiter.toFixed(2) : ""}
                  readOnly
                />
              </FormControl>
            </FormItem>

            <FormField
              control={form.control}
              name="provider_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Fornecedor</FormLabel>
                  <FormControl>
                    <FuelProviderSelect
                      value={selectedFuelProvider}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedFuelProvider(value);
                      }}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            {isDieselSelected && (
              <>
                <FormField
                  control={form.control}
                  name="arla_liters"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Litros de ARLA</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="0,00"
                          value={Number(field.value || 0)
                            .toFixed(3)
                            .replace(".", ",")}
                          onChange={(e) => {
                            let inputValue = e.target.value;
                            inputValue = inputValue.replace(/\D/g, "");
                            const newValue = inputValue
                              ? Number.parseInt(inputValue, 10) / 1000
                              : 0;
                            field.onChange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="arla_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço do ARLA (R$)</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          value={field.value ?? 0}
                          onChange={(value) =>
                            field.onChange(value ? Number(value) : null)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="invoice_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nota Fiscal</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date */}
            <DateField form={form} name="date" label="Data" />

            {/* Hour */}
            <FormField
              control={form.control}
              name="hour"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hora</FormLabel>
                  <FormControl>
                    <Input
                      type="time"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/fuellings")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default FuellingsForm;
